import {
  NavLink,
  Outlet,
  useNavigate,
  useLocation,
  Link,
} from 'react-router-dom';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  BookOpen,
  Users,
  Stethoscope,
  Settings,
  LogOut,
  List,
  ClipboardCheck,
  Menu,
  X,
  LayoutDashboard,
  Search,
  MessageSquareWarning,
  ScrollText,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import ProfileCompletionGate from '../ProfileCompletionGate';
import { doctorApi, userApi } from '../../api/admin';
import { reviewCommentsAdminApi } from '../../api/admin/ReviewCommentsApi';
import { useSocket } from '../../hooks/useSocket';
import { showInfoToast } from '../../utils/toast';
import {
  bumpApproverUnseen,
  clearApproverUnseen,
  readApproverUnseen,
  APPROVER_UNSEEN_CHANGED_EVENT,
} from '../../utils/adminApproverUnseen';

// Drop old broken "total − seen" key so it can't force badge back to 13
try {
  sessionStorage.removeItem('meraayurved:admin-approver-queue-seen');
} catch {
  // ignore
}

const AdminLayout = () => {
  const { logout, user, isImpersonating, impersonation, stopImpersonation } =
    useAuth();
  const socket = useSocket();
  const [pendingDoctorsCount, setPendingDoctorsCount] = useState(0);
  const [newUsersCount, setNewUsersCount] = useState(0);
  // Badge = unseen new submits only (same WS event as toast), NOT full queue size
  const [approverUnseenCount, setApproverUnseenCount] = useState(() =>
    readApproverUnseen(),
  );
  const [pendingCommentsCount, setPendingCommentsCount] = useState(0);
  const navigate = useNavigate();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('isAdminDesktopSidebarOpen');
    return saved !== null ? saved === 'true' : true;
  });
  const countsRequestIdRef = useRef(0);
  const focusDebounceRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    localStorage.setItem(
      'isAdminDesktopSidebarOpen',
      String(isDesktopSidebarOpen),
    );
  }, [isDesktopSidebarOpen]);
  const location = useLocation();
  const canApprove = Boolean(user?.can_approve || user?.canApprove);
  const canSeeApproverCounts = user?.role === 'admin' || canApprove;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate('/admin/dashboard');
  };

  useEffect(() => {
    const timer = setTimeout(() => setIsMobileSidebarOpen(false), 0);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Dedicated Approval Queue page = you've opened the queue
  useEffect(() => {
    if (!location.pathname.startsWith('/admin/approver-queue')) return;
    clearApproverUnseen();
    // Defer to satisfy react-hooks/set-state-in-effect lint.
    queueMicrotask(() => setApproverUnseenCount(0));
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/admin/user-comments')) {
      queueMicrotask(() => setPendingCommentsCount(0));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    if (location.pathname.startsWith('/admin/user-comments')) return;
    void reviewCommentsAdminApi
      .list({ page: 1, limit: 1, status: 'pending' })
      .then((res) => {
        if (res.success && res.data) {
          setPendingCommentsCount(
            res.data.pending_count ?? res.data.total ?? 0,
          );
        }
      });
  }, [user?.role, location.pathname]);

  // Keep sidebar badge in sync when Remedies tab clears/bumps unseen
  useEffect(() => {
    const onUnseenChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ count?: number }>).detail;
      if (typeof detail?.count === 'number') {
        setApproverUnseenCount(detail.count);
      } else {
        setApproverUnseenCount(readApproverUnseen());
      }
    };
    window.addEventListener(APPROVER_UNSEEN_CHANGED_EVENT, onUnseenChanged);
    return () => {
      window.removeEventListener(
        APPROVER_UNSEEN_CHANGED_EVENT,
        onUnseenChanged,
      );
    };
  }, []);

  const loadCounts = useCallback(async () => {
    const requestId = ++countsRequestIdRef.current;

    try {
      const [doctorRes, userRes] = await Promise.all([
        doctorApi.getPendingDoctorsCount(),
        userApi.getNewUsersCount(7),
      ]);

      if (requestId !== countsRequestIdRef.current) return;

      if (doctorRes.success && doctorRes.data?.count != null) {
        setPendingDoctorsCount(doctorRes.data.count);
      }
      if (userRes.success && userRes.data?.count != null) {
        setNewUsersCount(userRes.data.count);
      }
    } catch {
      // Keep last known badge values on error
    }
  }, []);

  useEffect(() => {
    // Defer to satisfy react-hooks/set-state-in-effect lint.
    queueMicrotask(() => {
      void loadCounts();
    });

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void loadCounts();
      }
    };

    const onFocus = () => {
      if (focusDebounceRef.current) {
        window.clearTimeout(focusDebounceRef.current);
      }
      focusDebounceRef.current = window.setTimeout(() => {
        void loadCounts();
      }, 400);
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('meraayurved:pending-doctors-changed', loadCounts);
    window.addEventListener('meraayurved:recipe-status-changed', loadCounts);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(
        'meraayurved:pending-doctors-changed',
        loadCounts,
      );
      window.removeEventListener(
        'meraayurved:recipe-status-changed',
        loadCounts,
      );
      if (focusDebounceRef.current) {
        window.clearTimeout(focusDebounceRef.current);
      }
    };
  }, [loadCounts]);

  // Same WS event as toast: bump unseen by 1 (never set from queue total)
  useEffect(() => {
    if (!socket) return;

    const onAppNotification = (payload: {
      kind?: string;
      message?: string;
      data?: { actorUserId?: number | string };
    }) => {
      const kind = payload?.kind;
      if (kind === 'recipe_queue' && canApprove) {
        setApproverUnseenCount(bumpApproverUnseen());
        void loadCounts();
        window.dispatchEvent(
          new CustomEvent('meraayurved:recipe-status-changed', {
            detail: {},
          }),
        );
      } else if (kind === 'pending_doctors') {
        void loadCounts();
        window.dispatchEvent(new Event('meraayurved:pending-doctors-changed'));
      } else if (kind === 'doctor_review_queue') {
        window.dispatchEvent(
          new Event('meraayurved:doctor-review-queue-changed'),
        );
      }
      // Dedupe with local action toasts; recipe_team_access is handled on remedy pages.
      if (payload?.message && kind !== 'recipe_team_access') {
        if (!(kind === 'recipe_queue' && !canApprove)) {
          showInfoToast(payload.message);
        }
      }
    };

    socket.on('app_notification', onAppNotification);
    const join = () => socket.emit('join_admin_review_comments');
    join();
    socket.on('connect', join);
    const onPendingComment = () => {
      setPendingCommentsCount((c) => c + 1);
    };
    const onModeratedComment = (payload?: { wasPending?: boolean }) => {
      if (payload && payload.wasPending === false) return;
      setPendingCommentsCount((c) => Math.max(0, c - 1));
    };
    socket.on('recipe_review_pending', onPendingComment);
    socket.on('recipe_review_approved', onModeratedComment);
    socket.on('recipe_review_rejected', onModeratedComment);
    return () => {
      socket.off('app_notification', onAppNotification);
      socket.off('connect', join);
      socket.off('recipe_review_pending', onPendingComment);
      socket.off('recipe_review_approved', onModeratedComment);
      socket.off('recipe_review_rejected', onModeratedComment);
      socket.emit('leave_admin_review_comments');
    };
  }, [socket, loadCounts, canApprove]);

  const toggleSidebar = () => {
    if (window.innerWidth >= 1024) {
      setIsDesktopSidebarOpen(!isDesktopSidebarOpen);
    } else {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    }
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    {
      path: '/admin/remedy',
      label: 'Remedies',
      icon: BookOpen,
      badge: canSeeApproverCounts ? approverUnseenCount : undefined,
    },
    { path: '/admin/remedy-traces', label: 'Save trace', icon: ScrollText },
    ...(canSeeApproverCounts
      ? [
          {
            path: '/admin/approver-queue',
            label: 'Approval Queue',
            icon: ClipboardCheck,
            badge: approverUnseenCount,
          },
        ]
      : []),
    { path: '/admin/user', label: 'Users', icon: Users, badge: newUsersCount },
    {
      path: '/admin/doctor',
      label: 'Doctors',
      icon: Stethoscope,
      badge: pendingDoctorsCount,
    },
    {
      path: '/admin/user-comments',
      label: 'User Comments',
      icon: MessageSquareWarning,
      badge: user?.role === 'admin' ? pendingCommentsCount : undefined,
    },
    { path: '/admin/manage-list', label: 'Manage Lists', icon: List },
    { path: '/admin/setting', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <ProfileCompletionGate />

      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-gray-200 shadow-xl lg:shadow-none transform transition-transform duration-300 ease-in-out 
          ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          ${isDesktopSidebarOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}
          font-body bg-white h-full`}
      >
        <div className="flex flex-col h-full bg-white">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-herbal-green to-herbal-green-dark flex items-center justify-center text-white text-sm font-semibold shadow-md shrink-0">
                {user?.name ? (
                  user.name
                    .split(' ')
                    .map((n: string) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)
                ) : (
                  <Users className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-semibold text-gray-900 font-heading">
                  Admin Panel
                </h1>
                {user && (
                  <p className="text-sm text-gray-500 mt-1 font-body truncate">
                    {user.name || user.email}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              className="lg:hidden p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 pt-6 pb-2">
            <Link
              to="/admin/search"
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`flex items-center gap-3 w-full px-4 py-2.5 border rounded-lg transition-colors font-body text-sm ${
                location.pathname.startsWith('/admin/search')
                  ? 'bg-herbal-green/10 border-herbal-green text-herbal-green'
                  : 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-500 hover:text-herbal-green'
              }`}
            >
              <Search className="w-4 h-4 shrink-0" />
              <span className="font-medium truncate">Search...</span>
            </Link>
          </div>

          <nav className="flex-1 px-4 pb-6 pt-2 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              const badge =
                'badge' in item
                  ? (item as { badge?: number }).badge
                  : undefined;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors duration-200 ease-out font-body ${
                      isActive
                        ? 'bg-herbal-green text-white shadow-sm'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="font-medium truncate">{item.label}</span>
                  {badge != null && badge > 0 && (
                    <span className="ml-auto shrink-0 min-w-[1.25rem] h-5 px-1.5 flex items-center justify-center rounded-full text-xs font-semibold bg-amber-500 text-white">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>

          <div className="p-4 border-t border-gray-200 bg-white">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors font-body"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <main
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${
          isDesktopSidebarOpen ? 'lg:ml-64' : 'lg:ml-0'
        }`}
      >
        {isImpersonating && (
          <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-amber-900 flex items-center justify-between gap-3">
            <p className="text-sm font-medium">
              Impersonating as user. Acting as{' '}
              {user?.name || user?.email || 'selected account'} (session:{' '}
              {impersonation?.impSessionId || 'n/a'}).
            </p>
            <button
              type="button"
              onClick={handleStopImpersonation}
              className="px-3 py-1.5 rounded-md bg-amber-900 text-amber-50 text-sm font-medium hover:bg-amber-800 transition-colors"
            >
              Stop impersonation
            </button>
          </div>
        )}
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={toggleSidebar}
              className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="font-semibold text-gray-900 font-heading">
              Admin Panel
            </span>
          </div>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-herbal-green to-herbal-green-dark flex items-center justify-center text-white text-xs font-semibold shadow-sm">
            {user?.name ? (
              user.name
                .split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2)
            ) : (
              <Users className="w-4 h-4" />
            )}
          </div>
        </header>

        <div className="p-4 lg:p-8 max-w-full overflow-x-hidden">
          <div
            key={location.pathname}
            className="animate-in fade-in slide-in-from-bottom-1 duration-200"
          >
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
